import React from 'react';
import { m } from 'motion/react';

// ---------------------------------------------------------------------------
// MAPPING code CN-CP-CA → id numérique (profil_individuel-2.html)
// Ordre dans le select : L=Low, M=Medium, H=High
// id 1→27 correspond à :
//  1=L-H-H  2=L-H-M  3=L-H-L  4=L-M-H  5=L-M-M  6=L-M-L
//  7=L-L-H  8=L-L-M  9=L-L-L  10=M-H-H 11=M-H-M 12=M-H-L
// 13=M-M-H 14=M-M-M 15=M-M-L 16=M-L-H 17=M-L-M 18=M-L-L
// 19=H-H-H 20=H-H-M 21=H-H-L 22=H-M-H 23=H-M-M 24=H-M-L
// 25=H-L-H 26=H-L-M 27=H-L-L
// ---------------------------------------------------------------------------

export const CODE_TO_ID: Record<string, number> = {
  'L-H-H': 1,  'L-H-M': 2,  'L-H-L': 3,
  'L-M-H': 4,  'L-M-M': 5,  'L-M-L': 6,
  'L-L-H': 7,  'L-L-M': 8,  'L-L-L': 9,
  'M-H-H': 10, 'M-H-M': 11, 'M-H-L': 12,
  'M-M-H': 13, 'M-M-M': 14, 'M-M-L': 15,
  'M-L-H': 16, 'M-L-M': 17, 'M-L-L': 18,
  'H-H-H': 19, 'H-H-M': 20, 'H-H-L': 21,
  'H-M-H': 22, 'H-M-M': 23, 'H-M-L': 24,
  'H-L-H': 25, 'H-L-M': 26, 'H-L-L': 27,
};

// ---------------------------------------------------------------------------
// DONNÉES DES 27 PROFILS (extraites de profil_individuel-2.html)
// ---------------------------------------------------------------------------

interface ProfileData {
  id: number;
  code: string;
  badge: string;
  name: string;
  intro: string;
  particularites: string[];
  vivez: { text: string; bold?: string }[];
  activeColor: string;
  activeBg: string;
  activeBorder: string;
}

export const PROFILE_DATA: ProfileData[] = [
  {
    id: 1, code: 'L-H-H',
    badge: 'Fermeture · Dorsal Pur',
    name: 'Le Naufragé Silencieux',
    intro: 'Le Naufragé Silencieux fonctionne en mode d\'économie d\'énergie extrême, là où la structure et l\'action se sont retirées pour survivre. Son indice de charge est estimé à 85/100. Sa particularité : s\'éteindre pour ne pas sombrer.',
    particularites: [
      'Une coupure presque totale des sensations corporelles pour éviter la surcharge.',
      'Une capacité à exécuter les tâches de manière purement automatique, sans y être présent.',
      'Un besoin vital de solitude et de silence, loin de toute sollicitation extérieure.',
    ],
    vivez: [
      { text: 'Le corps lourd comme du plomb.' },
      { text: 'Le regard qui se perd dans le vide.' },
      { text: 'La sensation d\'être ', bold: 'spectateur de sa propre vie' },
    ],
    activeColor: '#4A4038', activeBg: 'rgba(74,64,56,0.07)', activeBorder: 'rgba(74,64,56,0.20)',
  },
  {
    id: 2, code: 'L-H-M',
    badge: 'Fermeture · Dorsal Pur',
    name: 'La Brisée Portante',
    intro: 'La Brisée Portante navigue à la limite de la rupture structurelle, maintenant un semblant d\'activité alors que le corps réclame l\'arrêt complet. Son indice de charge est estimé à 78/100. Sa particularité : porter le poids du monde en silence.',
    particularites: [
      'Un sentiment de responsabilité immense qui pousse à continuer malgré l\'épuisement.',
      'Une structure lombaire et cervicale qui semble vide de tout soutien interne.',
      'Une fatigue de fond qui s\'efface temporairement dans l\'effort pour réapparaître plus lourde.',
    ],
    vivez: [
      { text: 'Une sensation d\'écrasement au niveau des épaules.' },
      { text: 'Un sommeil de plomb qui ne restaure plus rien.' },
      { text: 'La sensation physique que ', bold: 'le dos va se dérober au moindre pas' },
    ],
    activeColor: '#4A4038', activeBg: 'rgba(74,64,56,0.07)', activeBorder: 'rgba(74,64,56,0.20)',
  },
  {
    id: 3, code: 'L-H-L',
    badge: 'Fermeture · Dorsal Pur',
    name: 'Le Stoïque Épuisé',
    intro: 'Le Stoïque Épuisé fait face au vide énergétique en coupant le signal d\'alarme de ses muscles et de ses articulations. Son indice de charge est estimé à 72/100. Sa particularité : avancer sans ressentir.',
    particularites: [
      'Une absence apparente de douleur qui masque une détresse tissulaire profonde.',
      'Une rationalisation froide des limites physiques, repoussant sans cesse le repos.',
      'Une surprise totale lorsque le corps finit par se bloquer de manière aiguë.',
    ],
    vivez: [
      { text: 'Une sensation de tête coupée du reste du corps.' },
      { text: 'Des mouvements mécaniques et rigides, sans fluidité.' },
      { text: 'L\'impression d\'être ', bold: 'anesthésié face à ses propres besoins' },
    ],
    activeColor: '#4A4038', activeBg: 'rgba(74,64,56,0.07)', activeBorder: 'rgba(74,64,56,0.20)',
  },
  {
    id: 4, code: 'L-M-H',
    badge: 'Contenir · Mélangé',
    name: 'Le Blessé Total',
    intro: 'Le Blessé Total encaisse une charge émotionnelle et physique intense en verrouillant sa structure pour ne rien laisser paraître. Son indice de charge est estimé à 68/100. Sa particularité : blinder le corps pour protéger le cœur.',
    particularites: [
      'Une tension musculaire continue, notamment au niveau du diaphragme et du bassin.',
      'Une hypersensibilité aux critiques ou aux conflits, vécue comme une agression physique.',
      'Une vigilance constante pour maintenir la façade de contrôle face aux autres.',
    ],
    vivez: [
      { text: 'Une respiration haute, courte et constamment retenue.' },
      { text: 'Une boule douloureuse logée au creux de l\'estomac.' },
      { text: 'La sensation d\'être ', bold: 'une cocotte-minute sur le point d\'exploser' },
    ],
    activeColor: '#6F7B70', activeBg: 'rgba(111,123,112,0.08)', activeBorder: 'rgba(111,123,112,0.25)',
  },
  {
    id: 5, code: 'L-M-M',
    badge: 'Contenir · Mélangé',
    name: 'L\'Équilibre Mesuré',
    intro: 'L\'Équilibre Mesuré navigue avec prudence entre action et récupération, contenant ses tensions pour préserver son efficacité. Son indice de charge est estimé à 62/100. Sa particularité : doser chaque effort avec précision.',
    particularites: [
      'Une régulation consciente de son énergie pour éviter les débordements.',
      'Un besoin de clarté et d\'ordre dans son environnement pour se sentir en sécurité.',
      'Une réticence à exprimer ses doutes ou ses faiblesses pour ne pas perturber l\'équilibre.',
    ],
    vivez: [
      { text: 'Une légère raideur dans la nuque qui s\'installe au fil de la journée.' },
      { text: 'Une sensation de contrôle permanent sur ses gestes et ses paroles.' },
      { text: 'Le besoin d\'un ', bold: 'temps de décompression indispensable le soir' },
    ],
    activeColor: '#6F7B70', activeBg: 'rgba(111,123,112,0.08)', activeBorder: 'rgba(111,123,112,0.25)',
  },
  {
    id: 6, code: 'L-M-L',
    badge: 'Contenir · Mélangé',
    name: 'Le Travailleur Régulier',
    intro: 'Le Travailleur Régulier fonctionne là où activation et sécurité coexistent. Son indice de charge est estimé à 58/100. Sa particularité : contenir pour tenir.',
    particularites: [
      'Fiable et régulier dans l\'effort, même quand personne ne le voit.',
      'Capable de tenir sans montrer. Une forme de solidité rare.',
      'Sensible à l\'injustice et aux tensions relationnelles, même non exprimées.',
    ],
    vivez: [
      { text: 'Une boule dans la gorge.' },
      { text: 'Une mâchoire serrée.' },
      { text: 'La sensation d\'être ', bold: 'prête à exploser au moindre imprévu' },
    ],
    activeColor: '#6F7B70', activeBg: 'rgba(111,123,112,0.08)', activeBorder: 'rgba(111,123,112,0.25)',
  },
  {
    id: 7, code: 'L-L-H',
    badge: 'Flow · Ventral Pur',
    name: 'L\'Effondrement Total',
    intro: 'L\'Effondrement Total représente la libération complète et soudaine de tensions accumulées, ouvrant la voie à une reconstruction profonde. Son indice de charge est estimé à 45/100. Sa particularité : lâcher prise pour renaître.',
    particularites: [
      'Un abandon complet des mécanismes de défense et de contrôle devenus inutiles.',
      'Une vulnérabilité assumée, vécue comme une force de reconnexion à soi.',
      'Un besoin de repos passif et de sommeil réparateur pour recharger les batteries.',
    ],
    vivez: [
      { text: 'Des larmes de soulagement qui coulent sans raison apparente.' },
      { text: 'Une sensation de vide immense mais paisible dans la poitrine.' },
      { text: 'L\'impression que ', bold: 'le corps se dépose enfin sur le sol' },
    ],
    activeColor: '#4A7C59', activeBg: 'rgba(74,124,89,0.08)', activeBorder: 'rgba(74,124,89,0.25)',
  },
  {
    id: 8, code: 'L-L-M',
    badge: 'Flow · Ventral Pur',
    name: 'L\'Épuisé Dissocié',
    intro: 'L\'Épuisé Dissocié glisse doucement vers la sécurité en acceptant de ressentir la fatigue que son esprit avait masquée. Son indice de charge est estimé à 35/100. Sa particularité : accueillir la fatigue pour guérir.',
    particularites: [
      'Une transition douce vers le calme, où le corps réclame enfin son dû.',
      'Une baisse de la vigilance mentale qui permet aux émotions de circuler librement.',
      'Un ralentissement bienveillant du rythme quotidien pour se synchroniser sur le corps.',
    ],
    vivez: [
      { text: 'Une fatigue saine et profonde qui invite au sommeil.' },
      { text: 'Une sensation de relâchement musculaire dans tout le corps.' },
      { text: 'Le plaisir simple de ', bold: 'ne rien faire sans culpabiliser' },
    ],
    activeColor: '#4A7C59', activeBg: 'rgba(74,124,89,0.08)', activeBorder: 'rgba(74,124,89,0.25)',
  },
  {
    id: 9, code: 'L-L-L',
    badge: 'Flow · Ventral Pur',
    name: 'L\'État de Flux',
    intro: 'L\'État de Flux incarne l\'harmonie neuro-somatique absolue, où la vitalité et la sécurité coexistent sans aucun effort. Son indice de charge est estimé à 12/100. Sa particularité : la résilience par la fluidité.',
    particularites: [
      'Une respiration naturelle, calme et spontanément diaphragmatique.',
      'Une présence attentive et ouverte aux autres, sans aucune posture défensive.',
      'Une adaptabilité immédiate aux imprévus, vécus comme des jeux ou des opportunités.',
    ],
    vivez: [
      { text: 'Une sensation de légèreté et de liberté de mouvement.' },
      { text: 'Une clarté d\'esprit et une créativité joyeuse au quotidien.' },
      { text: 'La certitude tranquille que ', bold: 'le corps est à sa juste place' },
    ],
    activeColor: '#4A7C59', activeBg: 'rgba(74,124,89,0.08)', activeBorder: 'rgba(74,124,89,0.25)',
  },
  {
    id: 10, code: 'M-H-H',
    badge: 'Pression · Sympathique',
    name: 'L\'Urgence Systémique',
    intro: 'L\'Urgence Systémique vit sous une tension constante, mobilisant une énergie de combat immense qui ne trouve aucun canal de sortie. Son indice de charge est estimé à 88/100. Sa particularité : lutter contre l\'invisible.',
    particularites: [
      'Un corps en alerte rouge permanente, prêt à réagir au moindre signal.',
      'Une accumulation de frustrations physiques et nerveuses qui sature les muscles.',
      'Une incapacité biologique à désactiver la vigilance, même dans un lieu sûr.',
    ],
    vivez: [
      { text: 'Des battements cardiaques perceptibles au repos.' },
      { text: 'Une sensation de brûlure ou d\'oppression dans la poitrine.' },
      { text: 'L\'impression d\'être ', bold: 'enfermé dans une armure de fer trop étroite' },
    ],
    activeColor: '#B94040', activeBg: 'rgba(185,64,64,0.07)', activeBorder: 'rgba(185,64,64,0.20)',
  },
  {
    id: 11, code: 'M-H-M',
    badge: 'Pression · Sympathique',
    name: 'Le Performeur au Bord',
    intro: 'Le Performeur au Bord utilise l\'action intensive pour contenir une surcharge structurelle qui menace de s\'effondrer. Son indice de charge est estimé à 82/100. Sa particularité : courir pour ne pas tomber.',
    particularites: [
      'Un surinvestissement professionnel ou physique pour masquer la fatigue.',
      'Des blocages articulaires fréquents, notamment au niveau lombaire.',
      'Une peur inconsciente de l\'inactivité, assimilée à une perte de contrôle.',
    ],
    vivez: [
      { text: 'Une impatience irritée face au moindre ralentissement.' },
      { text: 'Des muscles fessiers et lombaires constamment contractés.' },
      { text: 'La sensation d\'avancer ', bold: 'sur un fil tendu au-dessus du vide' },
    ],
    activeColor: '#B94040', activeBg: 'rgba(185,64,64,0.07)', activeBorder: 'rgba(185,64,64,0.20)',
  },
  {
    id: 12, code: 'M-H-L',
    badge: 'Pression · Sympathique',
    name: 'Le Résistant Silencieux',
    intro: 'Le Résistant Silencieux encaisse la pression en figeant ses mouvements pour ne pas laisser déborder sa colère ou sa frustration. Son indice de charge est estimé à 75/100. Sa particularité : absorber les chocs sans broncher.',
    particularites: [
      'Une solidité apparente qui inspire confiance mais use l\'organisme en secret.',
      'Un refus de demander de l\'aide, perçu comme un aveu de faiblesse.',
      'Une tension accumulée dans les mâchoires et les muscles de la déglutition.',
    ],
    vivez: [
      { text: 'Des grincements de dents nocturnes (bruxisme).' },
      { text: 'Une raideur douloureuse le long de la colonne vertébrale.' },
      { text: 'Le sentiment de devoir ', bold: 'porter tout le poids de la situation seule' },
    ],
    activeColor: '#B94040', activeBg: 'rgba(185,64,64,0.07)', activeBorder: 'rgba(185,64,64,0.20)',
  },
  {
    id: 13, code: 'M-M-H',
    badge: 'Adaptation · Mélangé',
    name: 'Le Stoïque Dissocié',
    intro: 'Le Stoïque Dissocié s\'adapte en coupant la connexion avec son corps, maintenant un niveau d\'engagement élevé malgré un épuisement sous-jacent. Son indice de charge est estimé à 70/100. Sa particularité : fonctionner à crédit.',
    particularites: [
      'Une efficacité intellectuelle intacte qui masque la détresse somatique.',
      'Une ignorance systématique des signaux de douleur ou de fatigue.',
      'Une déconnexion émotionnelle qui complique les relations intimes.',
    ],
    vivez: [
      { text: 'La sensation d\'être une tête pensante posée sur un corps absent.' },
      { text: 'Une fatigue immense qui surgit d\'un coup dès que l\'action s\'arrête.' },
      { text: 'L\'impression de ', bold: 'jouer un rôle parfaitement écrit mais sans âme' },
    ],
    activeColor: '#6B9FD4', activeBg: 'rgba(107,159,212,0.07)', activeBorder: 'rgba(107,159,212,0.25)',
  },
  {
    id: 14, code: 'M-M-M',
    badge: 'Adaptation · Mélangé',
    name: 'Le Performeur Stable',
    intro: 'Le Performeur Stable maintient un rythme soutenu en compensant ses tensions par une volonté de fer et une organisation sans faille. Son indice de charge est estimé à 64/100. Sa particularité : la régularité à tout prix.',
    particularites: [
      'Une fiabilité exemplaire, très valorisée dans le cadre professionnel.',
      'Une habitude de planifier chaque détail pour éviter l\'imprévu stressant.',
      'Une tendance à minimiser ses propres besoins au profit de ceux du collectif.',
    ],
    vivez: [
      { text: 'Une tension diffuse dans le haut du dos et les trapèzes.' },
      { text: 'Un besoin constant de rester actif pour se sentir valorisé.' },
      { text: 'La sensation que ', bold: 'le repos doit être mérité par l\'effort' },
    ],
    activeColor: '#6B9FD4', activeBg: 'rgba(107,159,212,0.07)', activeBorder: 'rgba(107,159,212,0.25)',
  },
  {
    id: 15, code: 'M-M-L',
    badge: 'Adaptation · Mélangé',
    name: 'Le Gestionnaire Équilibré',
    intro: 'Le Gestionnaire Équilibré organise sa vie pour contenir le stress, mais dépense une énergie considérable à maintenir cet équilibre artificiel. Son indice de charge est estimé à 60/100. Sa particularité : manager son propre système.',
    particularites: [
      'Une recherche constante de compromis et de solutions d\'apaisement.',
      'Une sensibilité aux variations de son niveau d\'énergie, qu\'il tente de corriger.',
      'Une peur de perdre le contrôle qui l\'amène à sur-analyser ses ressentis.',
    ],
    vivez: [
      { text: 'Une sensation de fatigue mentale légère mais persistante.' },
      { text: 'Des tensions musculaires changeantes selon les jours.' },
      { text: 'Le sentiment de devoir ', bold: 'sans cesse réajuster les plateaux de la balance' },
    ],
    activeColor: '#6B9FD4', activeBg: 'rgba(107,159,212,0.07)', activeBorder: 'rgba(107,159,212,0.25)',
  },
  {
    id: 16, code: 'M-L-H',
    badge: 'Stoïcisme · Mélangé',
    name: 'Le Figé Vigilant',
    intro: 'Le Figé Vigilant reste immobile et silencieux, observant le monde avec méfiance alors que son corps est verrouillé par la peur. Son indice de charge est estimé à 65/100. Sa particularité : l\'observation sans action.',
    particularites: [
      'Une immobilité physique protectrice pour passer inaperçu.',
      'Un regard vif et attentif au moindre mouvement autour de soi.',
      'Une respiration très lente et superficielle, presque imperceptible.',
    ],
    vivez: [
      { text: 'Le corps froid, en particulier les mains et les pieds.' },
      { text: 'Une sensation de vide ou de coton dans la tête.' },
      { text: 'L\'impression d\'être ', bold: 'invisible et totalement déconnecté du présent' },
    ],
    activeColor: '#4A7C59', activeBg: 'rgba(74,124,89,0.08)', activeBorder: 'rgba(74,124,89,0.25)',
  },
  {
    id: 17, code: 'M-L-M',
    badge: 'Stoïcisme · Mélangé',
    name: 'Le Fatigué Vigilant',
    intro: 'Le Fatigué Vigilant traîne un épuisement profond tout en maintenant ses capteurs sensoriels ouverts pour parer à toute urgence. Son indice de charge est estimé à 55/100. Sa particularité : veiller malgré la fatigue.',
    particularites: [
      'Un manque d\'élan vital combiné à une incapacité à lâcher prise.',
      'Une sensibilité accrue aux bruits soudains ou aux changements de lumière.',
      'Une tendance à ruminer le soir, empêchant un endormissement serein.',
    ],
    vivez: [
      { text: 'Des paupières lourdes mais un esprit qui tourne en boucle.' },
      { text: 'Une sensation de lassitude physique générale.' },
      { text: 'L\'impression d\'être ', bold: 'un gardien de nuit fatigué qui ne peut pas dormir' },
    ],
    activeColor: '#4A7C59', activeBg: 'rgba(74,124,89,0.08)', activeBorder: 'rgba(74,124,89,0.25)',
  },
  {
    id: 18, code: 'M-L-L',
    badge: 'Stoïcisme · Mélangé',
    name: 'Le Curieux Attentif',
    intro: 'Le Curieux Attentif explore son environnement avec douceur, utilisant sa sensibilité pour tisser des liens sécurisants. Son indice de charge est estimé à 48/100. Sa particularité : la connexion par la présence.',
    particularites: [
      'Une écoute empathique et profonde des besoins des autres.',
      'Un intérêt pour les approches somatiques, artistiques ou contemplatives.',
      'Une recherche d\'authenticité et de justesse dans ses relations.',
    ],
    vivez: [
      { text: 'Une respiration calme et des mouvements plutôt lents.' },
      { text: 'Une sensation d\'ouverture et de réceptivité sensorielle.' },
      { text: 'Le sentiment d\'être ', bold: 'pleinement ancré dans l\'instant présent' },
    ],
    activeColor: '#4A7C59', activeBg: 'rgba(74,124,89,0.08)', activeBorder: 'rgba(74,124,89,0.25)',
  },
  {
    id: 19, code: 'H-H-H',
    badge: 'Surcharge · Crise Imminente',
    name: 'La Crise Systémique',
    intro: 'La Crise Systémique traverse une tempête neuro-somatique totale, où toutes les alarmes du corps hurlent en même temps. Son indice de charge est estimé à 95/100. Sa particularité : la saturation absolue.',
    particularites: [
      'Une douleur diffuse et aiguë, rendant le moindre mouvement insupportable.',
      'Une panique physique incontrôlable, déconnectée de toute pensée logique.',
      'Une incapacité totale à tolérer le moindre stimulus (bruit, lumière, parole).',
    ],
    vivez: [
      { text: 'Des tremblements ou des spasmes musculaires réflexes.' },
      { text: 'La sensation que le cœur va exploser ou s\'arrêter.' },
      { text: 'L\'impression terrifiante de ', bold: 'perdre totalement le contrôle de son corps' },
    ],
    activeColor: '#B94040', activeBg: 'rgba(185,64,64,0.07)', activeBorder: 'rgba(185,64,64,0.20)',
  },
  {
    id: 20, code: 'H-H-M',
    badge: 'Surcharge · Crise Imminente',
    name: 'Le Burnout Naissant',
    intro: 'Le Burnout Naissant glisse inexorablement vers l\'épuisement total, ses dernières forces de compensation s\'effondrant les unes après les autres. Son indice de charge est estimé à 90/100. Sa particularité : l\'effondrement des barrières.',
    particularites: [
      'Une fatigue matinale écrasante, que même dix heures de sommeil ne soulagent pas.',
      'Une irritabilité ou des crises de larmes soudaines face à des détails anodins.',
      'Un sentiment de vide intérieur et de découragement face aux tâches quotidiennes.',
    ],
    vivez: [
      { text: 'Une sensation de brouillard mental épais et permanent.' },
      { text: 'Des vertiges ou des pertes d\'équilibre lors des changements de position.' },
      { text: 'La sensation que ', bold: 'chaque geste demande un effort surhumain' },
    ],
    activeColor: '#B94040', activeBg: 'rgba(185,64,64,0.07)', activeBorder: 'rgba(185,64,64,0.20)',
  },
  {
    id: 21, code: 'H-H-L',
    badge: 'Surcharge · Crise Imminente',
    name: 'Le Combattant Hypervigilant',
    intro: 'Le Combattant Hypervigilant utilise ses dernières réserves d\'adrénaline pour maintenir une posture de lutte, refusant de voir l\'abîme de fatigue qui s\'ouvre sous ses pieds. Son indice de charge est estimé à 84/100. Sa particularité : la résistance désespérée.',
    particularites: [
      'Une attitude défensive et combative, prête au conflit pour se protéger.',
      'Une tension extrême dans le dos, vécue comme une armure contre les agressions.',
      'Un sommeil haché par des réveils en sursaut et des sueurs nocturnes.',
    ],
    vivez: [
      { text: 'Une mâchoire serrée à s\'en fêler les dents.' },
      { text: 'Une sensation de brûlure acide dans l\'estomac.' },
      { text: 'La certitude permanente que ', bold: 'le moindre relâchement sera fatal' },
    ],
    activeColor: '#B94040', activeBg: 'rgba(185,64,64,0.07)', activeBorder: 'rgba(185,64,64,0.20)',
  },
  {
    id: 22, code: 'H-M-H',
    badge: 'Vigilance · Sympathique',
    name: 'Le Robot Épuisé',
    intro: 'Le Robot Épuisé fonctionne de manière purement mécanique, exécutant ses tâches avec une précision froide pour masquer un épuisement émotionnel total. Son indice de charge est estimé à 80/100. Sa particularité : l\'automatisme défensif.',
    particularites: [
      'Une absence d\'expression émotionnelle, vécue comme une neutralité professionnelle.',
      'Une obsession pour les détails et les procédures pour éviter toute erreur.',
      'Un refus inconscient de ressentir son corps pour ne pas s\'effondrer.',
    ],
    vivez: [
      { text: 'Des mouvements saccadés et sans aucune fluidité.' },
      { text: 'Une sensation de sécheresse générale (yeux, bouche, peau).' },
      { text: 'L\'impression d\'être ', bold: 'une machine programmée pour tourner jusqu\'à l\'usure' },
    ],
    activeColor: '#6B9FD4', activeBg: 'rgba(107,159,212,0.07)', activeBorder: 'rgba(107,159,212,0.25)',
  },
  {
    id: 23, code: 'H-M-M',
    badge: 'Vigilance · Sympathique',
    name: 'Le Performeur Anxieux',
    intro: 'Le Performeur Anxieux utilise l\'action frénétique et la réussite sociale comme des boucliers pour calmer une anxiété viscérale qui le ronge de l\'intérieur. Son indice de charge est estimé à 76/100. Sa particularité : la réussite comme anxiolytique.',
    particularites: [
      'Un besoin impérieux de reconnaissance et de validation par l\'extérieur.',
      'Une peur panique de l\'échec, assimilé à une mise en danger vitale.',
      'Une incapacité à déléguer ou à faire confiance, par peur de perdre le contrôle.',
    ],
    vivez: [
      { text: 'Une boule d\'angoisse qui monte et descend le long de la poitrine.' },
      { text: 'Des palpitations cardiaques dès que l\'activité ralentit.' },
      { text: 'Le besoin de ', bold: 'toujours en faire plus pour se sentir digne d\'exister' },
    ],
    activeColor: '#6B9FD4', activeBg: 'rgba(107,159,212,0.07)', activeBorder: 'rgba(107,159,212,0.25)',
  },
  {
    id: 24, code: 'H-M-L',
    badge: 'Vigilance · Sympathique',
    name: 'L\'Anxieux Fonctionnel',
    intro: 'L\'Anxieux Fonctionnel assure toutes ses responsabilités avec brio, cachant derrière un sourire et une efficacité redoutable un ouragan d\'inquiétude permanent. Son indice de charge est estimé à 70/100. Sa particularité : l\'angoisse invisible.',
    particularites: [
      'Une préparation excessive à tous les scénarios catastrophes possibles.',
      'Une gentillesse et une serviabilité extrêmes pour s\'assurer de la bienveillance d\'autrui.',
      'Une fatigue mentale intense, causée par le flot continu de pensées anxieuses.',
    ],
    vivez: [
      { text: 'Une respiration haute et rapide, bloquée au niveau des côtes.' },
      { text: 'Des tensions diffuses dans le cou et les épaules.' },
      { text: 'La sensation de devoir ', bold: 'porter un masque de sérénité de plus en plus lourd' },
    ],
    activeColor: '#6B9FD4', activeBg: 'rgba(107,159,212,0.07)', activeBorder: 'rgba(107,159,212,0.25)',
  },
  {
    id: 25, code: 'H-L-H',
    badge: 'Performance · Sympathique',
    name: 'Le Traumatisé Figé',
    intro: 'Le Traumatisé Figé est bloqué dans une double contrainte neuro-somatique : une mobilisation sympathique maximale piégée dans une structure dorsale qui refuse de bouger. Son indice de charge est estimé à 86/100. Sa particularité : l\'immobilité sous haute tension.',
    particularites: [
      'Une sensation d\'être pétrifié sur place tout en ressentant une panique interne immense.',
      'Des douleurs chroniques intenses et rebelles à tout traitement classique.',
      'Une dissociation fréquente, où l\'esprit s\'échappe pour ne pas ressentir la détresse.',
    ],
    vivez: [
      { text: 'Le corps glacé et rigide comme du bois.' },
      { text: 'Une incapacité à crier ou à appeler à l\'aide.' },
      { text: 'La sensation d\'être ', bold: 'prisonnier de son propre corps en état d\'alerte' },
    ],
    activeColor: '#B94040', activeBg: 'rgba(185,64,64,0.07)', activeBorder: 'rgba(185,64,64,0.20)',
  },
  {
    id: 26, code: 'H-L-M',
    badge: 'Performance · Sympathique',
    name: 'L\'Anxieux Épuisé',
    intro: 'L\'Anxieux Épuisé a consommé toutes ses réserves d\'action, mais son système nerveux refuse de désactiver l\'alarme, le maintenant dans un état de panique sans énergie. Son indice de charge est estimé à 81/100. Sa particularité : l\'alerte à vide.',
    particularites: [
      'Une fatigue extrême combinée à une agitation nerveuse insupportable.',
      'Des difficultés d\'attention et de mémoire, causées par la saturation du système.',
      'Une vulnérabilité émotionnelle totale, où la moindre contrariété provoque un effondrement.',
    ],
    vivez: [
      { text: 'Un tremblement intérieur continu et épuisant.' },
      { text: 'Des sueurs froides et des sensations de vertige.' },
      { text: 'L\'impression d\'être ', bold: 'un moteur qui tourne à vide et menace de serrer' },
    ],
    activeColor: '#B94040', activeBg: 'rgba(185,64,64,0.07)', activeBorder: 'rgba(185,64,64,0.20)',
  },
  {
    id: 27, code: 'H-L-L',
    badge: 'Performance · Sympathique',
    name: 'Le Flottant Anxieux',
    intro: 'Le Flottant Anxieux navigue dans un état de déconnexion légère mais permanente, utilisant le rêve ou la distraction pour fuir une réalité corporelle trop douloureuse. Son indice de charge est estimé à 74/100. Sa particularité : l\'évitement sensoriel.',
    particularites: [
      'Une difficulté à rester concentré sur une tâche ou une conversation.',
      'Une maladresse physique fréquente (objets qui tombent, chocs légers).',
      'Un sentiment d\'irréalité, comme si le monde extérieur était derrière une vitre.',
    ],
    vivez: [
      { text: 'Une sensation de flottement ou d\'apesanteur corporelle.' },
      { text: 'Des difficultés à situer son corps dans l\'espace.' },
      { text: 'L\'impression de ', bold: 'marcher sur un nuage de coton instable' },
    ],
    activeColor: '#B94040', activeBg: 'rgba(185,64,64,0.07)', activeBorder: 'rgba(185,64,64,0.20)',
  },
];

// ---------------------------------------------------------------------------
// COMPOSANT PROFILESCREEN
// ---------------------------------------------------------------------------

interface ProfileScreenProps {
  code: string;
  score: number;
  onCTA: () => void;
}

export default function ProfileScreen({ code, score, onCTA }: ProfileScreenProps) {
  const id = CODE_TO_ID[code] ?? 6;
  const profile = PROFILE_DATA.find(p => p.id === id) ?? PROFILE_DATA[5];

  // Score de charge (0 = optimal, 100 = crise) → inverser pour affichage
  const chargeScore = Math.max(0, 100 - score);

  return (
    <m.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5 }}
      className="w-full"
    >
      {/* Carte principale */}
      <div
        className="bg-white border rounded-[24px] p-8 shadow-[0_2px_8px_rgba(28,27,27,0.06)]"
        style={{ borderColor: '#EBDED2' }}
      >
        {/* Badge état */}
        <div className="mb-6">
          <span
            className="inline-flex items-center gap-1 font-sans text-[11px] font-medium tracking-[0.12em] uppercase rounded-full px-4 py-1"
            style={{
              color: profile.activeColor,
              background: profile.activeBg,
              border: `1px solid ${profile.activeBorder}`,
            }}
          >
            {profile.badge}
          </span>
        </div>

        {/* Nom du profil */}
        <p className="font-serif text-[clamp(26px,5vw,32px)] font-light text-ink leading-[1.15] tracking-[-0.01em] mb-4">
          {profile.name}
        </p>

        {/* Intro */}
        <p className="font-sans text-[15px] font-semibold text-ink leading-[1.7] mb-0">
          {profile.intro}
        </p>

        {/* Séparateur */}
        <div className="h-px bg-[#EBDED2] my-6" />

        {/* Particularités */}
        <span className="block font-sans text-[11px] font-medium tracking-[0.15em] uppercase text-taupe mb-4">
          Ses particularités
        </span>
        <ul className="flex flex-col gap-2 mb-0">
          {profile.particularites.map((item, i) => (
            <li key={i} className="font-sans text-[15px] text-ink leading-[1.65] pl-4 relative">
              <span
                className="absolute left-0 top-[9px] w-[5px] h-[5px] rounded-full"
                style={{ background: profile.activeColor, opacity: 0.55 }}
              />
              {item}
            </li>
          ))}
        </ul>

        {/* Ce que vous vivez */}
        <div className="mt-6 mb-8">
          <span className="block font-sans text-[11px] font-medium tracking-[0.15em] uppercase text-taupe mb-4">
            Ce que vous vivez peut-être
          </span>
          <div className="flex flex-col gap-2">
            {profile.vivez.map((v, i) => (
              <p key={i} className="font-sans text-[15px] italic text-ink/65 leading-[1.65] pl-4 relative">
                <span
                  className="absolute left-0 top-[9px] w-[5px] h-[5px] rounded-full"
                  style={{ background: profile.activeColor, opacity: 0.35 }}
                />
                {v.text}
                {v.bold && <strong className="font-semibold italic text-ink">{v.bold}</strong>}
                {v.bold && '.'}
              </p>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-2">
          <span className="block font-sans text-[11px] font-medium tracking-[0.15em] uppercase text-taupe text-left mb-4">
            Votre exercice de régulation
          </span>
          <button
            onClick={onCTA}
            className="block w-full text-center font-sans text-[14px] font-medium tracking-[0.03em] text-white rounded-full py-[14px] px-8 min-h-[44px] transition-all duration-200 hover:opacity-90 active:scale-[0.98]"
            style={{ background: '#2B5F8E' }}
          >
            Obtenir votre exercice personnalisé →
          </button>
          <div className="flex justify-center items-center gap-2 flex-wrap mt-4">
            {['3 minutes', 'Guidé', 'Aucune préparation'].map(pill => (
              <span
                key={pill}
                className="inline-flex items-center gap-[5px] font-sans text-[11px] font-medium tracking-[0.06em] text-taupe bg-paper border border-[#EBDED2] rounded-full px-[10px] py-1"
              >
                <span className="w-1 h-1 rounded-full bg-[#D4C4B0] flex-shrink-0" />
                {pill}
              </span>
            ))}
          </div>
        </div>
      </div>
    </m.div>
  );
}
